<template>
  <RouterLink
    v-if="to"
    :to="to"
    :class="rootClasses"
    @click="handleAnchorClick"
  >
    <font-awesome-icon v-if="loading" :icon="['fal', 'spinner']" spin />
    <font-awesome-icon v-else-if="icon && icon.trim()" :icon="[iconPrefix, cleanIconName]" />
    <slot />
  </RouterLink>
  <a
    v-else-if="href"
    :href="href"
    :class="rootClasses"
    :aria-disabled="disabled ? 'true' : undefined"
    @click="handleAnchorClick"
  >
    <font-awesome-icon v-if="loading" :icon="['fal', 'spinner']" spin />
    <font-awesome-icon v-else-if="icon && icon.trim()" :icon="[iconPrefix, cleanIconName]" />
    <slot />
  </a>
  <button
    v-else
    type="button"
    :class="rootClasses"
    :disabled="disabled"
    @click="handleClick"
  >
    <font-awesome-icon v-if="loading" :icon="['fal', 'spinner']" spin />
    <font-awesome-icon v-else-if="icon && icon.trim()" :icon="[iconPrefix, cleanIconName]" />
    <slot />
  </button>
</template>

<script setup>
import { useSlots, computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  icon: {
    type: String,
    required: true,
    validator: (value) => value && value.trim().length > 0,
  },
  iconPrefix: {
    type: String,
    default: 'fal',
  },
  type: {
    type: String,
    default: 'default',
  },
  // Eksplisitt prop som overstyrer auto-heuristikken. Når null/undefined:
  // icon-only-knapper (uten slot-tekst) er round som standard; knapper med
  // slot-tekst er rounded-md.
  round: {
    type: Boolean,
    default: null,
  },
  // Visuell størrelse: 'small' (28×28, p-1), 'default' (36×36, p-2), 'large' (44×44, p-3).
  // Gjelder kun icon-only-rendering; med slot-tekst er størrelsen padding-driven.
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['small', 'default', 'large'].includes(value),
  },
  // Vue Router-target. Når satt: rendres som <RouterLink>. Foretrekkes
  // fremfor `href` for intern navigasjon (bevarer middle-click + SPA-routing).
  to: {
    type: [String, Object],
    default: null,
  },
  // Native href — rendres som <a>. For ekstern navigasjon eller mailto/tel.
  href: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const hasSlotContent = computed(() => !!slots.default);
// Icon-only-knapper er round som standard (designkonvensjon).
// Caller kan overstyre via `round`-prop (true/false).
const isRound = computed(() => {
  if (props.round !== null) return props.round;
  return !hasSlotContent.value;
});

const cleanIconName = computed(() => props.icon.replace(/^fa-/, ''));

// Per-type color classes keyed by button type. off = disabled, on = enabled.
// Unknown types resolve to TYPE_FALLBACK, mirroring the original switch default branch.
// The on-accent variant targets buttons on the dark accent gradient (AppHeader,
// on-gradient banners): light text with a subtle white hover-bg and no border.
const TYPE_CLASSES = new Map([
  ['primary', {
    off: 'text-accent',
    on: 'text-accent hover:bg-accent hover:text-fg-on-accent'
  }],
  ['cancel', {
    off: 'text-fg-muted',
    on: 'text-fg-muted hover:bg-fg-muted hover:text-fg-on-accent'
  }],
  ['default', {
    off: 'text-fg-muted',
    on: 'text-fg-muted hover:bg-fg-muted hover:text-fg-on-accent'
  }],
  ['delete', {
    off: 'text-danger',
    on: 'text-danger hover:bg-danger hover:text-fg-on-accent'
  }],
  ['info', {
    off: 'text-info',
    on: 'text-info hover:bg-info hover:text-fg-on-accent'
  }],
  ['warning', {
    off: 'text-warning',
    on: 'text-warning hover:bg-warning hover:text-fg-on-accent'
  }],
  ['danger', {
    off: 'text-danger',
    on: 'text-danger hover:bg-danger hover:text-fg-on-accent'
  }],
  ['environment', {
    off: 'text-accent bg-surface',
    on: 'text-accent bg-surface hover:bg-accent hover:text-fg-on-accent'
  }],
  ['on-accent', {
    off: 'text-fg-on-accent/60',
    on: 'text-fg-on-accent hover:bg-fg-on-accent/20 focus-visible:bg-fg-on-accent/20'
  }]
]);
const TYPE_FALLBACK = {
  off: 'text-fg',
  on: 'text-fg hover:bg-fg/20 focus-visible:bg-fg/20'
};

const typeClasses = computed(() => {
  const entry = TYPE_CLASSES.get(props.type) ?? TYPE_FALLBACK;
  return props.disabled ? entry.off : entry.on;
});

const sizeClasses = computed(() => {
  // Icon-only-modus får fikset bredde/høyde; med slot er sizingen padding-driven.
  if (!hasSlotContent.value) {
    switch (props.size) {
      case 'small': return 'w-7 h-7 p-1 text-sm justify-center';
      case 'large': return 'w-11 h-11 p-3 text-lg justify-center';
      default: return 'w-9 h-9 p-2 text-base justify-center';
    }
  }
  switch (props.size) {
    case 'small': return 'p-1 text-sm';
    case 'large': return 'p-3 text-lg';
    default: return 'p-2 text-base';
  }
});

const rootClasses = computed(() => [
  'inline-flex items-center gap-2 transition-colors no-underline',
  'border-0 bg-transparent appearance-none focus:outline-none',
  sizeClasses.value,
  isRound.value ? 'rounded-full' : 'rounded-md',
  props.disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
  typeClasses.value,
]);

const emit = defineEmits(['click']);

function handleClick (event) {
  if (props.disabled) return;
  emit('click', event);
}

function handleAnchorClick (event) {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emit('click', event);
}
</script>
